import { Datagrid, TextField, NumberField, Title, useGetIdentity, useGetList, useNotify, DateField, FunctionField, useRecordContext } from 'react-admin';
import { Box, Button, Typography, Stack, Skeleton, Chip, Tabs, Tab } from '@mui/material';
import { useState, useEffect, useMemo, useRef, type SyntheticEvent } from 'react';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { DatagridBody, DatagridRow } from 'ra-ui-materialui/dist/esm/list/datagrid';
import type { DatagridRowProps } from 'ra-ui-materialui/dist/esm/list/datagrid';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { adLocksApi, AdLock } from './adLocks';
import { canAccessExpertModerationDetails } from './accessControl';

interface ModerationAd {
    id: string;
    lastUpdateDate: string;
    aiModerationState?: string;
    adDetails: {
        countOfPhoto: number;
        brandName: string;
        modelName: string;
        price: string;
    };
    meta?: {
        cursor?: string | null;
    };
}

type ExpertModerationSlaStatus = 'none' | 'warning' | 'critical';
type AdsModerationType = 'FIRST_MODERATION' | 'FIRST_AI_MODERATION';
type AiModerationState = 'PENDING' | 'PROCESSING' | 'DONE' | 'FAILED' | 'NEED_MANUAL_CHECK';
type AiModerationStatusTone = 'warning' | 'info' | 'success' | 'error' | 'unknown';

type ExitingAiAd = {
    ad: ModerationAd;
    order: number;
    leaving: boolean;
};

const ADS_MODERATION_TABS: Array<{ value: AdsModerationType; label: string }> = [
    { value: 'FIRST_MODERATION', label: 'Ручная модерация' },
    { value: 'FIRST_AI_MODERATION', label: 'Модерация AI' }
];

const EXPERT_MODERATION_WARNING_MS = 6 * 60 * 60 * 1000;
const EXPERT_MODERATION_CRITICAL_MS = 12 * 60 * 60 * 1000;
const EXPERT_MODERATION_BLOCKED_TOOLTIP = 'Нельзя проверить объявления, пока не будут проверены красные и желтые объявления';
const AI_MODERATION_POLL_INTERVAL_MS = 5000;
const AI_MODERATION_EXIT_VISIBLE_MS = 5000;
const AI_MODERATION_EXIT_FADE_MS = 500;

const AI_MODERATION_STATE_META: Record<AiModerationState, { label: string; tone: AiModerationStatusTone }> = {
    PENDING: { label: 'Ожидается к проверке', tone: 'warning' },
    PROCESSING: { label: 'Проверяется AI', tone: 'info' },
    DONE: { label: 'Успешно проверено', tone: 'success' },
    FAILED: { label: 'Ошибка при проверке', tone: 'error' },
    NEED_MANUAL_CHECK: { label: 'Требуется ручная проверка', tone: 'error' }
};

const getDateMs = (dateString?: string | null) => {
    if (!dateString) {
        return null;
    }

    const dateMs = new Date(dateString).getTime();
    return Number.isNaN(dateMs) ? null : dateMs;
};

const getExpertModerationSlaStatus = (lastUpdateDate: string | undefined, nowMs: number): ExpertModerationSlaStatus => {
    const updatedMs = getDateMs(lastUpdateDate);
    if (updatedMs === null) {
        return 'none';
    }

    const elapsedMs = nowMs - updatedMs;
    if (elapsedMs >= EXPERT_MODERATION_CRITICAL_MS) {
        return 'critical';
    }
    if (elapsedMs >= EXPERT_MODERATION_WARNING_MS) {
        return 'warning';
    }
    return 'none';
};

const getExpertModerationOverdueMs = (lastUpdateDate: string | undefined, nowMs: number) => {
    const updatedMs = getDateMs(lastUpdateDate);
    if (updatedMs === null) {
        return 0;
    }

    return Math.max(0, nowMs - updatedMs - EXPERT_MODERATION_WARNING_MS);
};

const formatOverdueDuration = (durationMs: number) => {
    const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours} ч. ${minutes} м. ${seconds} сек.`;
};

const formatUpdateDate = (dateString?: string | null) => {
    const dateMs = getDateMs(dateString);
    if (dateMs === null) {
        return '';
    }

    return new Date(dateMs).toLocaleString('ru-RU');
};

const getUniqueAdsById = (ads: ModerationAd[]) => {
    const seenIds = new Set<string>();

    return ads.filter((ad) => {
        if (!ad.id) {
            return true;
        }

        if (seenIds.has(ad.id)) {
            return false;
        }

        seenIds.add(ad.id);
        return true;
    });
};

const getAiModerationStatusMeta = (state?: string | null, isExiting = false) => {
    if (isExiting) {
        return { label: 'Проверено', tone: 'success' as AiModerationStatusTone };
    }

    const normalizedState = typeof state === 'string' ? state.trim() : '';
    if (!normalizedState) {
        return { label: 'Статус не указан', tone: 'unknown' as AiModerationStatusTone };
    }

    return AI_MODERATION_STATE_META[normalizedState as AiModerationState] || {
        label: normalizedState,
        tone: 'unknown' as AiModerationStatusTone
    };
};

const isExpertModerationSlaOverdue = (record: ModerationAd | undefined, nowMs: number) => (
    getExpertModerationSlaStatus(record?.lastUpdateDate, nowMs) !== 'none'
);

const isExpertModerationRowBlocked = (record: ModerationAd | undefined, hasOverdueAds: boolean, nowMs: number) => (
    hasOverdueAds && !isExpertModerationSlaOverdue(record, nowMs)
);

type ExpertModerationDatagridRowProps = DatagridRowProps & {
    hasOverdueAds: boolean;
    isReadOnly: boolean;
    nowMs: number;
};

const ExpertModerationDatagridRow = ({ hasOverdueAds, isReadOnly, nowMs, ...props }: ExpertModerationDatagridRowProps) => {
    const record = useRecordContext<ModerationAd>();
    const isBlocked = isExpertModerationRowBlocked(record, hasOverdueAds, nowMs);
    const isDisabled = isReadOnly || isBlocked;

    return (
        <DatagridRow
            {...props}
            aria-disabled={isDisabled || undefined}
            title={isReadOnly
                ? 'Просмотр карточки экспертной модерации недоступен для вашей роли'
                : isBlocked ? EXPERT_MODERATION_BLOCKED_TOOLTIP : undefined}
        />
    );
};

type AdsModerationTabContentProps = {
    moderationType: AdsModerationType;
    hidden: boolean;
};

const AdsModerationTabContent = ({
    moderationType,
    hidden
}: AdsModerationTabContentProps) => {
    const [cursor, setCursor] = useState<string | undefined>(undefined);
    const [allAds, setAllAds] = useState<ModerationAd[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const notify = useNotify();
    const [locksByAdId, setLocksByAdId] = useState<Record<string, AdLock | null>>({});
    const [exitingAiAdsById, setExitingAiAdsById] = useState<Record<string, ExitingAiAd>>({});
    const previousAiBaseAdsRef = useRef<ModerationAd[] | null>(null);
    const aiExitTimeoutsRef = useRef<Record<string, number[]>>({});

    const {
        data,
        isLoading,
        refetch
    } = useGetList<ModerationAd>(
        'adsModeration',
        {
            pagination: { page: 1, perPage: 30 },
            sort: { field: 'lastUpdateDate', order: 'DESC' },
            meta: { cursor, moderationType }
        },
        { enabled: !hidden }
    );

    const isManualModeration = moderationType === 'FIRST_MODERATION';
    const isAiModeration = moderationType === 'FIRST_AI_MODERATION';
    const panelId = `ads-moderation-tabpanel-${moderationType}`;
    const tabId = `ads-moderation-tab-${moderationType}`;

    useEffect(() => {
        setCursor(undefined);
        setAllAds([]);
        setHasMore(true);
        setLocksByAdId({});
        setExitingAiAdsById({});
        previousAiBaseAdsRef.current = null;
    }, [moderationType]);

    useEffect(() => {
        if (hidden) {
            return;
        }

        if (data && data.length > 0) {
            const lastAd = data[data.length - 1];
            const newCursor = lastAd.meta?.cursor;
            setHasMore(newCursor !== null && newCursor !== undefined);
        } else {
            setHasMore(false);
        }
    }, [data, hidden]);

    const handleLoadMore = () => {
        if (data && data.length > 0) {
            const lastAd = data[data.length - 1];
            const newCursor = lastAd.meta?.cursor;
            if (newCursor) {
                setAllAds(prev => [...prev, ...data]);
                setCursor(newCursor);
            } else {
                setHasMore(false);
            }
        }
    };

    const handleCopyId = (id: string) => {
        navigator.clipboard.writeText(id);
        notify('ID скопирован в буфер обмена', { type: 'success' });
    };

    const currentAds = useMemo(() => data || [], [data]);
    const baseDisplayAds = useMemo(() => getUniqueAdsById([...allAds, ...currentAds]), [allAds, currentAds]);
    const displayAds = useMemo(() => {
        if (!isAiModeration) {
            return baseDisplayAds;
        }

        const displayAdsWithExiting = [...baseDisplayAds];
        const displayIds = new Set(displayAdsWithExiting.map((ad) => ad.id));
        Object.entries(exitingAiAdsById)
            .sort(([, left], [, right]) => left.order - right.order)
            .forEach(([adId, exitingAd]) => {
                if (displayIds.has(adId)) {
                    return;
                }

                displayAdsWithExiting.splice(
                    Math.min(exitingAd.order, displayAdsWithExiting.length),
                    0,
                    exitingAd.ad
                );
                displayIds.add(adId);
            });

        return displayAdsWithExiting;
    }, [baseDisplayAds, exitingAiAdsById, isAiModeration]);
    const displayAdIds = useMemo(
        () => Array.from(new Set(displayAds.map((ad) => ad.id).filter(Boolean))),
        [displayAds]
    );

    useEffect(() => {
        if (!isAiModeration || hidden) {
            previousAiBaseAdsRef.current = null;
            return;
        }

        const previousAds = previousAiBaseAdsRef.current;
        if (!previousAds) {
            previousAiBaseAdsRef.current = baseDisplayAds;
            return;
        }

        const nextIds = new Set(baseDisplayAds.map((ad) => ad.id));
        setExitingAiAdsById((prev) => {
            const next = { ...prev };
            let hasChanges = false;

            nextIds.forEach((adId) => {
                if (!adId || !next[adId]) {
                    return;
                }

                aiExitTimeoutsRef.current[adId]?.forEach((timeoutId) => window.clearTimeout(timeoutId));
                delete aiExitTimeoutsRef.current[adId];
                delete next[adId];
                hasChanges = true;
            });

            return hasChanges ? next : prev;
        });

        previousAds.forEach((ad, order) => {
            if (!ad.id || nextIds.has(ad.id) || aiExitTimeoutsRef.current[ad.id]) {
                return;
            }

            setExitingAiAdsById((prev) => ({
                ...prev,
                [ad.id]: {
                    ad,
                    order,
                    leaving: false
                }
            }));

            const startLeavingTimeout = window.setTimeout(() => {
                setExitingAiAdsById((prev) => {
                    const existing = prev[ad.id];
                    if (!existing) {
                        return prev;
                    }

                    return {
                        ...prev,
                        [ad.id]: {
                            ...existing,
                            leaving: true
                        }
                    };
                });
            }, AI_MODERATION_EXIT_VISIBLE_MS - AI_MODERATION_EXIT_FADE_MS);

            const removeTimeout = window.setTimeout(() => {
                setExitingAiAdsById((prev) => {
                    const next = { ...prev };
                    delete next[ad.id];
                    return next;
                });
                delete aiExitTimeoutsRef.current[ad.id];
            }, AI_MODERATION_EXIT_VISIBLE_MS);

            aiExitTimeoutsRef.current[ad.id] = [startLeavingTimeout, removeTimeout];
        });

        previousAiBaseAdsRef.current = baseDisplayAds;
    }, [baseDisplayAds, hidden, isAiModeration]);

    useEffect(() => {
        if (!isAiModeration || hidden) {
            return;
        }

        const intervalId = window.setInterval(() => {
            refetch().catch((error) => {
                console.error('Failed to poll AI moderation ads', error);
            });
        }, AI_MODERATION_POLL_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [hidden, isAiModeration, refetch]);

    useEffect(() => {
        return () => {
            Object.values(aiExitTimeoutsRef.current).forEach((timeouts) => {
                timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
            });
            aiExitTimeoutsRef.current = {};
        };
    }, []);

    useEffect(() => {
        let isActive = true;
        let intervalId: number | undefined;

        const syncLocks = async () => {
            if (hidden) {
                return;
            }

            if (!displayAdIds.length) {
                if (isActive) {
                    setLocksByAdId({});
                }
                return;
            }

            try {
                const locks = await adLocksApi.getMany('adsModeration', displayAdIds);
                if (isActive) {
                    setLocksByAdId(locks);
                }
            } catch (error) {
                if (isActive) {
                    console.error('Failed to sync moderation locks', error);
                }
            }
        };

        syncLocks();
        if (!hidden) {
            intervalId = window.setInterval(syncLocks, 5000);
        }

        return () => {
            isActive = false;
            if (intervalId) {
                window.clearInterval(intervalId);
            }
        };
    }, [displayAdIds, hidden]);

    const getModerationRowSx = (record: ModerationAd) => {
        if (!isAiModeration) {
            return {};
        }

        const exitingAd = record?.id ? exitingAiAdsById[record.id] : undefined;
        if (!exitingAd) {
            return {};
        }

        return (theme: Theme) => {
            const isLeaving = exitingAd?.leaving === true;

            return {
                opacity: isLeaving ? 0 : 1,
                transform: isLeaving ? 'translateY(-6px)' : 'translateY(0)',
                transition: theme.transitions.create(['opacity', 'transform'], {
                    duration: AI_MODERATION_EXIT_FADE_MS,
                    easing: theme.transitions.easing.easeInOut
                }),
                '& > td': {
                    transition: theme.transitions.create(['border-bottom-width'], {
                        duration: AI_MODERATION_EXIT_FADE_MS,
                        easing: theme.transitions.easing.easeInOut
                    }),
                    ...(isLeaving ? {
                        borderBottomWidth: 0
                    } : {})
                }
            };
        };
    };

    const getAiModerationChipColor = (tone: AiModerationStatusTone) => {
        if (tone === 'warning') {
            return 'warning';
        }
        if (tone === 'info') {
            return 'info';
        }
        if (tone === 'success') {
            return 'success';
        }
        if (tone === 'error') {
            return 'error';
        }
        return 'default';
    };

    const getAiModerationChipSx = (state: string | undefined, isExiting: boolean, tone: AiModerationStatusTone) => (
        (theme: Theme) => {
            const normalizedState = state?.trim();
            const isPending = !isExiting && normalizedState === 'PENDING';
            const isProcessing = !isExiting && normalizedState === 'PROCESSING';
            const chipColor = tone === 'unknown'
                ? theme.palette.text.secondary
                : theme.palette[tone].main;

            return {
                minWidth: 140,
                justifyContent: 'center',
                fontWeight: 600,
                letterSpacing: 0,
                ...(isPending ? {
                    animation: 'aiModerationChipPulse 1.4s ease-in-out infinite'
                } : {}),
                ...(isProcessing ? {
                    color: theme.palette.common.white,
                    backgroundColor: chipColor,
                    backgroundImage: `linear-gradient(90deg, ${alpha(chipColor, 0)} 0%, ${alpha(theme.palette.common.white, 0.18)} 35%, ${alpha(theme.palette.common.white, 0.38)} 50%, ${alpha(theme.palette.common.white, 0.18)} 65%, ${alpha(chipColor, 0)} 100%)`,
                    backgroundSize: '180% 100%',
                    animation: 'aiModerationChipLoading 1.6s ease-in-out infinite'
                } : {}),
                '@keyframes aiModerationChipPulse': {
                    '0%, 100%': {
                        boxShadow: `0 0 0 0 ${alpha(chipColor, 0.28)}`,
                        opacity: 0.86
                    },
                    '50%': {
                        boxShadow: `0 0 0 6px ${alpha(chipColor, 0)}`,
                        opacity: 1
                    }
                },
                '@keyframes aiModerationChipLoading': {
                    '0%': {
                        backgroundPosition: '100% 0'
                    },
                    '50%': {
                        backgroundPosition: '0% 0'
                    },
                    '100%': {
                        backgroundPosition: '100% 0'
                    }
                }
            };
        }
    );

    const LoadingSkeleton = () => (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={1}>
                {[...Array(5)].map((_, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Skeleton variant="text" width={200} />
                        <Skeleton variant="text" width={150} />
                        <Skeleton variant="text" width={180} />
                        <Skeleton variant="text" width={80} />
                        <Skeleton variant="text" width={60} />
                        <Skeleton variant="text" width={100} />
                    </Box>
                ))}
            </Stack>
        </Box>
    );

    if (hidden) {
        return (
            <Box
                id={panelId}
                role="tabpanel"
                aria-labelledby={tabId}
                hidden
            />
        );
    }

    if (isLoading && allAds.length === 0) {
        return (
            <Box
                id={panelId}
                role="tabpanel"
                aria-labelledby={tabId}
            >
                <Box sx={{ mt: 2 }}>
                    <LoadingSkeleton />
                </Box>
            </Box>
        );
    }

    if (displayAds.length === 0 && !isLoading) {
        return (
            <Box
                id={panelId}
                role="tabpanel"
                aria-labelledby={tabId}
                sx={{ p: 3, textAlign: 'center' }}
            >
                <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                    {isManualModeration
                        ? 'Список объявлений на ручную проверку пуст'
                        : 'Список объявлений на проверке AI пуст'}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            id={panelId}
            role="tabpanel"
            aria-labelledby={tabId}
        >
            <Box sx={{ mt: 2 }}>
                <Datagrid 
                    data={displayAds}
                    rowClick="show"
                    rowSx={getModerationRowSx}
                    bulkActionButtons={false}
                    sx={{ '& .MuiTableCell-root': { py: 2 } }}
                >
                    {isAiModeration && (
                        <FunctionField
                            label="AI статус"
                            render={record => {
                                const isExiting = record?.id ? Boolean(exitingAiAdsById[record.id]) : false;
                                const statusMeta = getAiModerationStatusMeta(record?.aiModerationState, isExiting);

                                return (
                                    <Chip
                                        size="small"
                                        color={getAiModerationChipColor(statusMeta.tone)}
                                        label={statusMeta.label}
                                        sx={getAiModerationChipSx(record?.aiModerationState, isExiting, statusMeta.tone)}
                                    />
                                );
                            }}
                        />
                    )}
                    <TextField 
                        source="id" 
                        label="ID" 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopyId(e.currentTarget.textContent || '');
                        }}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    />
                    <DateField source="lastUpdateDate" label="Дата обновления" showTime />
                    <NumberField source="adDetails.countOfPhoto" label="Кол-во фото" />
                    <TextField source="adDetails.brandName" label="Название бренда" />
                    <TextField source="adDetails.modelName" label="Название модели" />
                    <FunctionField
                        label="Цена"
                        render={record => {
                            const price = record?.adDetails?.price;
                            if (!price) return '';
                            const formatted = Number(price).toLocaleString('ru-RU');
                            return `${formatted} ₽`;
                        }}
                    />
                    {!isAiModeration && (
                        <FunctionField
                            label="Статус"
                            render={record => {
                                if (!record?.lastUpdateDate) return null;
                                const updated = new Date(record.lastUpdateDate);
                                const now = new Date();
                                const diffMs = now.getTime() - updated.getTime();
                                const diffH = diffMs / (1000 * 60 * 60);
                                const diffM = Math.floor(diffMs / (1000 * 60));
                                if (diffH > 1 && diffH <= 2) {
                                    return (
                                        <Tooltip title={`Нужно промодерировать объявление. Задержка: ${diffM} мин.`}>
                                            <WarningAmberIcon sx={{ color: '#ffb300' }} />
                                        </Tooltip>
                                    );
                                }
                                if (diffH > 2) {
                                    return (
                                        <Tooltip title={`Срочно промодерировать! Задержка: ${diffM} мин.`}>
                                            <ReportProblemIcon sx={{ color: '#f44336' }} />
                                        </Tooltip>
                                    );
                                }
                                return null;
                            }}
                        />
                    )}
                    {!isAiModeration && (
                        <FunctionField
                            label="Проверка"
                            render={record => {
                                const lock = record?.id ? locksByAdId[record.id] : null;
                                if (!lock) {
                                    return null;
                                }
                                return (
                                    <Chip
                                        size="small"
                                        color="warning"
                                        label={`Проверяется: ${lock.login || 'unknown'}`}
                                    />
                                );
                            }}
                        />
                    )}
                </Datagrid>
            </Box>
            {hasMore && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                    <Button variant="contained" onClick={handleLoadMore} disabled={isLoading}>
                        {isLoading ? 'Загрузка...' : 'Загрузить еще'}
                    </Button>
                </Stack>
            )}
        </Box>
    );
}; 

export const AdsModerationList = () => {
    const [activeTab, setActiveTab] = useState<AdsModerationType>('FIRST_MODERATION');
    const [visitedTabs, setVisitedTabs] = useState<Set<AdsModerationType>>(
        () => new Set(['FIRST_MODERATION'])
    );

    const handleTabChange = (_event: SyntheticEvent, value: AdsModerationType) => {
        setActiveTab(value);
        setVisitedTabs((prev) => {
            if (prev.has(value)) {
                return prev;
            }

            const next = new Set(prev);
            next.add(value);
            return next;
        });
    };

    return (
        <Box>
            <Title title="Объявления на модерации" />
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="Вкладки модерации объявлений"
                >
                    {ADS_MODERATION_TABS.map((tab) => (
                        <Tab
                            key={tab.value}
                            id={`ads-moderation-tab-${tab.value}`}
                            aria-controls={`ads-moderation-tabpanel-${tab.value}`}
                            value={tab.value}
                            label={tab.label}
                        />
                    ))}
                </Tabs>
            </Box>
            {ADS_MODERATION_TABS.filter((tab) => visitedTabs.has(tab.value)).map((tab) => (
                <AdsModerationTabContent
                    key={tab.value}
                    moderationType={tab.value}
                    hidden={activeTab !== tab.value}
                />
            ))}
        </Box>
    );
};

export const AdsExpertModerationList = () => {
    const { identity } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const canOpenExpertModerationDetails = canAccessExpertModerationDetails(currentUser?.role);
    const [cursor, setCursor] = useState<string | undefined>(undefined);
    const [allAds, setAllAds] = useState<ModerationAd[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [locksByAdId, setLocksByAdId] = useState<Record<string, AdLock | null>>({});

    const {
        data,
        isLoading,
        refetch
    } = useGetList<ModerationAd>(
        'adsExpertModeration',
        {
            pagination: { page: 1, perPage: 30 },
            sort: { field: 'lastUpdateDate', order: 'DESC' },
            meta: { cursor }
        }
    );

    useEffect(() => {
        if (data && data.length > 0) {
            const lastAd = data[data.length - 1];
            const newCursor = lastAd.meta?.cursor;
            setHasMore(newCursor !== null && newCursor !== undefined);
        } else {
            setHasMore(false);
        }
    }, [data]);

    const handleLoadMore = async () => {
        if (data && data.length > 0) {
            const lastAd = data[data.length - 1];
            const newCursor = lastAd.meta?.cursor;
            if (newCursor) {
                setCursor(newCursor);
                setAllAds(prev => [...prev, ...data]);
                await refetch();
            } else {
                setHasMore(false);
            }
        }
    };

    const currentAds = useMemo(() => data || [], [data]);
    const displayAds = useMemo(() => [...allAds, ...currentAds], [allAds, currentAds]);
    const displayAdIds = useMemo(
        () => Array.from(new Set(displayAds.map((ad) => ad.id).filter(Boolean))),
        [displayAds]
    );
    const hasOverdueExpertModerationAds = useMemo(
        () => displayAds.some((ad) => isExpertModerationSlaOverdue(ad, nowMs)),
        [displayAds, nowMs]
    );

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setNowMs(Date.now());
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        let isActive = true;
        let intervalId: number | undefined;

        const syncLocks = async () => {
            if (!displayAdIds.length) {
                if (isActive) {
                    setLocksByAdId({});
                }
                return;
            }

            try {
                const locks = await adLocksApi.getMany('adsExpertModeration', displayAdIds);
                if (isActive) {
                    setLocksByAdId(locks);
                }
            } catch (error) {
                if (isActive) {
                    console.error('Failed to sync expert moderation locks', error);
                }
            }
        };

        syncLocks();
        intervalId = window.setInterval(syncLocks, 5000);

        return () => {
            isActive = false;
            if (intervalId) {
                window.clearInterval(intervalId);
            }
        };
    }, [displayAdIds]);

    const LoadingSkeleton = () => (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={1}>
                {[...Array(5)].map((_, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Skeleton variant="text" width={200} />
                        <Skeleton variant="text" width={150} />
                        <Skeleton variant="text" width={180} />
                        <Skeleton variant="text" width={80} />
                        <Skeleton variant="text" width={60} />
                        <Skeleton variant="text" width={100} />
                    </Box>
                ))}
            </Stack>
        </Box>
    );

    if (isLoading && allAds.length === 0) {
        return (
            <Box>
                <Title title="Экспертная модерация объявлений" />
                <Box sx={{ mt: 2 }}>
                    <LoadingSkeleton />
                </Box>
            </Box>
        );
    }

    if (displayAds.length === 0 && !isLoading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Title title="Экспертная модерация объявлений" />
                <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                    Список объявлений на экспертную модерацию пуст
                </Typography>
            </Box>
        );
    }

    const getExpertModerationRowSx = (record: ModerationAd) => {
        const status = getExpertModerationSlaStatus(record?.lastUpdateDate, nowMs);
        const isBlocked = isExpertModerationRowBlocked(record, hasOverdueExpertModerationAds, nowMs);
        const blockedSx = (isBlocked || !canOpenExpertModerationDetails)
            ? {
                cursor: 'not-allowed',
                '& > td': {
                    cursor: 'not-allowed'
                }
            }
            : {};

        if (status === 'warning') {
            return (theme: Theme) => ({
                ...blockedSx,
                backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.12),
                '& > td': {
                    ...(blockedSx['& > td'] ?? {}),
                    backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.12),
                    borderBottomColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.28)
                },
                '&:hover > td': {
                    backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.24 : 0.2)
                }
            });
        }

        if (status === 'critical') {
            return (theme: Theme) => ({
                ...blockedSx,
                backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
                '& > td': {
                    ...(blockedSx['& > td'] ?? {}),
                    backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
                    borderBottomColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.4 : 0.24)
                },
                '&:hover > td': {
                    backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.28 : 0.18)
                }
            });
        }

        return blockedSx;
    };

    const getExpertModerationRowClick = (_id: string | number, _resource: string, record: ModerationAd) => {
        if (!canOpenExpertModerationDetails || isExpertModerationRowBlocked(record, hasOverdueExpertModerationAds, nowMs)) {
            return false;
        }

        return 'show';
    };

    return (
        <Box>
            <Title title="Экспертная модерация объявлений" />
            <Box sx={{ mt: 2 }}>
                <Datagrid 
                    data={displayAds}
                    body={(
                        <DatagridBody
                            row={(
                                <ExpertModerationDatagridRow
                                    hasOverdueAds={hasOverdueExpertModerationAds}
                                    isReadOnly={!canOpenExpertModerationDetails}
                                    nowMs={nowMs}
                                />
                            )}
                        />
                    )}
                    rowClick={getExpertModerationRowClick}
                    rowSx={getExpertModerationRowSx}
                    bulkActionButtons={false}
                    sx={{ '& .MuiTableCell-root': { py: 2 } }}
                >
                    <FunctionField
                        label="Дата обновления"
                        render={record => {
                            const formattedDate = formatUpdateDate(record?.lastUpdateDate);
                            const status = getExpertModerationSlaStatus(record?.lastUpdateDate, nowMs);

                            if (!formattedDate) {
                                return null;
                            }

                            return (
                                <Box>
                                    <Typography variant="body2">{formattedDate}</Typography>
                                    {status !== 'none' ? (
                                        <Typography
                                            variant="caption"
                                            sx={(theme) => ({
                                                color: status === 'critical'
                                                    ? theme.palette.error[theme.palette.mode === 'dark' ? 'light' : 'dark']
                                                    : theme.palette.warning[theme.palette.mode === 'dark' ? 'light' : 'dark'],
                                                display: 'block',
                                                fontWeight: 600,
                                                mt: 0.5
                                            })}
                                        >
                                            Просрочено на {formatOverdueDuration(getExpertModerationOverdueMs(record?.lastUpdateDate, nowMs))}
                                        </Typography>
                                    ) : null}
                                </Box>
                            );
                        }}
                    />
                    <NumberField source="adDetails.countOfPhoto" label="Кол-во фото" />
                    <TextField source="adDetails.brandName" label="Название бренда" />
                    <TextField source="adDetails.modelName" label="Название модели" />
                    <FunctionField
                        label="Цена"
                        render={record => {
                            const price = record?.adDetails?.price;
                            if (!price) return '';
                            const formatted = Number(price).toLocaleString('ru-RU');
                            return `${formatted} ₽`;
                        }}
                    />
                    <FunctionField
                        label="Проверка"
                        render={record => {
                            const lock = record?.id ? locksByAdId[record.id] : null;
                            if (!lock) {
                                return null;
                            }
                            return (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={`Проверяется: ${lock.login || 'unknown'}`}
                                />
                            );
                        }}
                    />
                </Datagrid>
            </Box>
            {hasMore && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                    <Button variant="contained" onClick={handleLoadMore}>
                        Загрузить еще
                    </Button>
                </Stack>
            )}
        </Box>
    );
}; 
